/* eslint-disable complexity */
import React, { SyntheticEvent, useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { SxProps, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useSnackbarContext } from '@skillzet/components';

import axios from 'axios';
import { useLocation } from 'react-router-dom';

import { DISCORD_INVITE_LOCATION, DiscordInviteButton } from '@app/common/components/DiscordInvite';
import { SKILLZ_DOCS_BASE_URL } from '@app/common/constants';
// import { UFC_GAME_URL } from '@app/common/constants';
// import ufcRedLogo from '@app/imageAssets/sidebar/ufcRedLogo.svg';
// import ufcWhiteLogo from '@app/imageAssets/sidebar/ufcWhiteLogo.svg';
import { shouldViewPeekPage } from '@app/common/helpers';
import { snackbarErrorMessage } from '@app/common/helpers/snackbarHookHelper';
import { removeUtmFromLocalStorage } from '@app/common/setup/utmLocalStorageSetup';
import { store } from '@app/developerPortal';
import BlueEyeOpened from '@app/imageAssets/sidebar/blueEyeOpened.svg';
import DocumentationIcon from '@app/imageAssets/sidebar/documentationIcon.svg';
import DownloadIcon from '@app/imageAssets/sidebar/downloadIcon.svg';
import GamesIcon from '@app/imageAssets/sidebar/gamesIcon.svg';
import HomeIcon from '@app/imageAssets/sidebar/homeIcon.svg';
import LearningCenterIcon from '@app/imageAssets/sidebar/learningCenterIcon.svg';
import LogoutIcon from '@app/imageAssets/sidebar/logoutIcon.svg';
import ProfileIcon from '@app/imageAssets/sidebar/profileIcon.svg';
import StatementsIcon from '@app/imageAssets/sidebar/statementsIcon.svg';
import SupportIcon from '@app/imageAssets/sidebar/supportIcon.svg';
import TrendUpIcon from '@app/imageAssets/sidebar/trendUpIcon.svg';
import logo from '@app/imageAssets/skillz-navbar-logo.svg';
import whiteLogo from '@app/imageAssets/skillzWhiteLogo.png';

import { TNavBarConnectedProps } from '.';
import './styles.scss';

const CSS_PREFIX = 'portal-developer-nav-bar';
type TLinkType = string | (() => string);

interface INavBarItem {
  link: TLinkType;
  text: string;
  icon?: string;
  selector?: string;
  sx?: SxProps;
  sxIcon?: SxProps;
  sxText?: SxProps;
}

const LOGGED_IN_ITEMS: Record<string, INavBarItem> = {
  dashboard: {
    link: '/dashboard',
    text: 'Dashboard',
    icon: HomeIcon,
    selector: 'dashboard-link',
  },
  games: {
    link: '/your_games',
    text: 'Games',
    icon: GamesIcon,
    selector: 'games-link',
  },
  analytics: {
    link: '/analytics',
    text: 'Analytics',
    icon: TrendUpIcon,
    selector: 'analytics-link',
  },
  statement: {
    link: '/statements',
    text: 'Statements',
    icon: StatementsIcon,
    selector: 'statements-link',
  },
  documentation: {
    link: SKILLZ_DOCS_BASE_URL as TLinkType,
    text: 'Documentation',
    icon: DocumentationIcon,
    selector: 'documentation-link',
  },
  learningCenter: {
    link: '/learning_center',
    text: 'Learning Center',
    icon: LearningCenterIcon,
    selector: 'learning-center-link',
  },
  downloads: {
    link: '/downloads',
    text: 'Downloads',
    icon: DownloadIcon,
    selector: 'download-link',
  },
  support: {
    link: '/support/contact_us',
    text: 'Support',
    icon: SupportIcon,
    selector: 'support-link',
  },
  account: {
    link: '/account',
    text: 'Account',
    icon: ProfileIcon,
    selector: 'account-link',
  },
  logOut: {
    link: 'users/sign_out',
    text: 'Log Out',
    icon: LogoutIcon,
    selector: 'log-out-link',
  },
};

const LOGGED_OUT_ITEMS: Record<string, INavBarItem> = {
  signIn: {
    link: '/users/sign_in',
    text: '',
    icon: '',
  },
  signUp: {
    link: '/users/sign_up',
    text: '',
    icon: '',
  },
  home: {
    link: '/',
    text: '',
    icon: HomeIcon,
  },
};

const SKILLZ_SUPPORT_ITEMS: Record<string, INavBarItem> = {
  peekMode: {
    link: '/peek',
    text: 'Peek Mode',
    icon: BlueEyeOpened,
    selector: 'peek-link',
  },
};

// const SPECIAL_EVENT_ITEMS: Record<string, INavBarItem> = {
//   ufcPartnership: {
//     link: UFC_GAME_URL,
//     text: 'Partnership',
//     selector: 'ufc-partnership-link',
//     sx: {
//       ':hover': {
//         '.MuiListItemIcon-root': {
//           background: `url(${ufcWhiteLogo})`,
//           backgroundRepeat: 'no-repeat',
//         },
//         backgroundColor: '#d20a0a',
//       },
//     },
//     sxIcon: {
//       backgroundColor: '#2e323a',
//       background: `url(${ufcRedLogo})`,
//       backgroundRepeat: 'no-repeat',
//       minWidth: '72px',
//     },
//     sxText: {
//       marginLeft: '12px',
//     },
//   },
// };

const NAV_ITEM = {
  ...LOGGED_IN_ITEMS,
  ...LOGGED_OUT_ITEMS,
  ...SKILLZ_SUPPORT_ITEMS,
  // ...SPECIAL_EVENT_ITEMS,
};

function NavBar(props: TNavBarConnectedProps): JSX.Element {
  const { show: showSnackbar } = useSnackbarContext();

  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [currentNav, setCurrentNav] = useState<string | null>(null);

  const location = useLocation();
  const isLoggedIn = props.user.id !== undefined;

  // const games = props.games.items[0] || [];

  // const isAnyGameSandboxReady = games.some((game) => game.sandboxReady === STATUS.COMPLETED);

  const isDiscordFeatureEnabled = window.appConfig.featureFlags.discordSelfServe;

  const isOnUserPages = /^\/users/.test(location.pathname);
  const [hasScrolled, setScrolled] = React.useState(false);

  let isSignUpPage: boolean = location.pathname.includes('users/sign_up');
  let isSignInPage: boolean = location.pathname.includes('users/sign_in');
  let isForgotPasswordPage: boolean = location.pathname.includes('users/password/new');
  // let isRedesignedPage = isSignUpPage || isSignInPage || isForgotPasswordPage;

  // onscroll changes
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== hasScrolled) {
        setScrolled(!hasScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);

    return () => {
      // clean up the event listener when the component unmounts
      document.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  // update selected item on navigation
  useEffect(() => {
    const currentLocation = location.pathname.split('/')[1];

    const currenNavIdx = Object.values(NAV_ITEM).findIndex((item) => {
      if (typeof item.link !== 'string') return false;

      return item.link.includes(currentLocation);
    });
    const temp = Object.keys(NAV_ITEM)[currenNavIdx];
    if (currenNavIdx > -1 && temp) {
      setCurrentNav(temp);
    }
  }, [location]);

  const handleMenuClose = () => setMenuOpen(null);

  const handleMenuIconClick = (menuType: string) => {
    return (event: SyntheticEvent<unknown>) => {
      event.stopPropagation();

      const newMenuOpenValue = menuType === menuOpen ? null : menuType;

      setMenuOpen(newMenuOpenValue);
    };
  };

  const redirectUrl = (url: INavBarItem['link']) => {
    return (): void => {
      let urlString: string;
      if (typeof url === 'function') {
        urlString = url();
      } else {
        urlString = url;
      }

      if (urlString.startsWith('http')) {
        window.open(urlString);

        return;
      }

      if (urlString === props.location.pathname) return;

      if (urlString === 'users/sign_out') {
        void handleSignOut();

        return;
      }

      props.history.push(urlString);
    };
  };

  const handleSignOut = async () => {
    try {
      handleMenuClose();
      await axios.delete('/users/sign_out');

      store.dispatch({ type: 'RESET' });

      showSnackbar('Signed out successfully.', 'success');

      removeUtmFromLocalStorage();
      redirectUrl('/users/sign_in')();
    } catch (error) {
      showSnackbar(snackbarErrorMessage(error), 'error');
    }
  };

  const renderItems = (items: Record<string, INavBarItem>) =>
    Object.values(items).map((item, idx) => (
      <ListItemButton
        key={item.text}
        sx={{
          ':hover': {
            backgroundColor: '#414752',
            opacity: '1.0',
          },
          '&.Mui-selected': {
            backgroundColor: '#414752',
            ':hover': {
              backgroundColor: '#414752',
              opacity: '1.0',
            },
          },
          ...item.sx,
        }}
        onClick={() => {
          redirectUrl(item.link)();
          handleMenuClose();
        }}
        selected={NAV_ITEM[currentNav ?? 'home'] === item}
        id={item.selector}
        data-qa={item.selector}
      >
        <ListItemIcon sx={{ height: '24px', marginRight: '-16px', ...item.sxIcon }}>
          {item.icon && <Box component="img" id={`icon-${idx}`} src={item.icon} />}
        </ListItemIcon>
        <ListItemText
          sx={{ fontSize: '16px', fontWeight: '500', color: 'white', ...item.sxText }}
          primary={item.text}
          disableTypography={true}
        />
      </ListItemButton>
    ));

  const renderNavMenu = () => {
    // const specialEventItems: Record<string, INavBarItem> = SPECIAL_EVENT_ITEMS;

    const drawer = (
      <Box
        role="presentation"
        className="portal-developer-nav-bar-presentation-box"
        data-qa="portal-developer-nav-bar-presentation-box"
      >
        <Box
          sx={(theme) => ({
            height: theme.appBar.navBarHeight.small,
            justifyContent: 'center',
            alignItems: 'center',
            display: { xs: 'none', md: 'flex' },
          })}
        >
          <Box
            component="img"
            sx={{ height: 25 }}
            alt="Skillz logo"
            id="skillz-logo-left-nav"
            src={isSignUpPage || isSignInPage || isForgotPasswordPage ? whiteLogo : logo}
            onClick={
              isOnUserPages
                ? redirectUrl(LOGGED_OUT_ITEMS.home.link)
                : isLoggedIn
                ? redirectUrl(LOGGED_IN_ITEMS.dashboard.link)
                : undefined
            }
          />
        </Box>
        <List>
          {renderItems(LOGGED_IN_ITEMS)}
          {shouldViewPeekPage(props.user, props.publisher) && renderItems(SKILLZ_SUPPORT_ITEMS)}
          <Divider sx={{ borderWidth: '1px', borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          <ListItem sx={{ paddingTop: '24px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: '#B8BDC7' }}>
              SPECIAL EVENTS:
            </Typography>
          </ListItem>
          {/* {renderItems(specialEventItems)} */}
        </List>
        {isDiscordFeatureEnabled && (
          <DiscordInviteButton location={DISCORD_INVITE_LOCATION.NAVBAR} />
        )}
      </Box>
    );

    return (
      <>
        <Drawer
          variant="temporary"
          open={menuOpen === 'nav'}
          onClose={handleMenuClose}
          ModalProps={{
            keepMounted: true, // better open performance on mobile.
          }}
          sx={(theme) => ({
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: theme.appBar.drawerWidth,
              boxShadow: 'none',
            },
          })}
          classes={{
            paper: 'left-drawer-paper style-override peek-header-offset',
            modal: 'peek-header-offset',
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={(theme) => ({
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: theme.appBar.drawerWidth },
          })}
          classes={{ paper: 'left-drawer-paper style-override peek-header-offset' }}
          open
        >
          {drawer}
        </Drawer>
      </>
    );
  };

  const appBarClass = `portal-developer-app-bar peek-header-offset no-navbar-offset new-nav-style-override ${
    hasScrolled ? 'new-nav-style-override-scrolled' : ''
  }`;

  return (
    <>
      <AppBar
        className={appBarClass}
        data-qa="portal-developer-app-bar"
        sx={(theme) => ({
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          p: '5px 15px !important',
          height: isLoggedIn ? theme.appBar.navBarHeight.small : theme.appBar.navBarHeight.large,
          // topbar is hidden on md and up when user logged in
          display: { xs: 'flex', md: isLoggedIn ? 'none' : 'flex' },
        })}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flex: isLoggedIn ? '1 0 100%' : '0 1 70%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isLoggedIn && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMenuIconClick('nav')}
            >
              {menuOpen !== 'nav' ? <MenuIcon /> : <CloseIcon />}
            </IconButton>
          )}
          {/* logo is in the middle when user logged in */}
          <Box sx={{ flex: isLoggedIn ? 1 : 0, display: 'flex', justifyContent: 'center' }}>
            <img
              src={isSignUpPage || isSignInPage || isForgotPasswordPage ? whiteLogo : logo}
              id="skillz-logo"
              height={isLoggedIn ? 20 : 25}
              onClick={
                isOnUserPages
                  ? redirectUrl(LOGGED_OUT_ITEMS.home.link)
                  : isLoggedIn
                  ? redirectUrl(LOGGED_IN_ITEMS.dashboard.link)
                  : undefined
              }
              style={isOnUserPages ? { cursor: 'pointer' } : {}}
            />
          </Box>

          {(!isOnUserPages || isSignInPage || isSignUpPage) && !isLoggedIn && (
            <Box data-qa="home-page__navbar-links" alignItems="flex-end" display="flex">
              {!isSignUpPage && (
                <Button
                  id={`${CSS_PREFIX}__button-signup`}
                  className="home-page__navbar-button-overhaul home-page__navbar-button-overhaul-signup"
                  variant="contained"
                  onClick={redirectUrl(LOGGED_OUT_ITEMS.signUp.link)}
                  data-qa={`${CSS_PREFIX}__button-signup`}
                  size={'medium'}
                  sx={(theme) => ({
                    width: '128px',
                    marginLeft: '20px',
                    [theme.breakpoints.down('sm')]: {
                      display: 'none',
                    },
                    [theme.breakpoints.up('sm')]: {
                      display: 'block',
                    },
                  })}
                >
                  SIGN UP
                </Button>
              )}
              {!isSignInPage && (
                <Button
                  id={`${CSS_PREFIX}__button-login`}
                  className="home-page__navbar-button-overhaul"
                  variant="text"
                  color="info"
                  onClick={redirectUrl(LOGGED_OUT_ITEMS.signIn.link)}
                  data-qa={`${CSS_PREFIX}__button-login`}
                  size={'medium'}
                  sx={(theme) => ({
                    color: theme.palette.primary.contrastText,
                    [theme.breakpoints.down('sm')]: {
                      display: 'none',
                    },
                    [theme.breakpoints.up('sm')]: {
                      display: 'block',
                    },
                  })}
                >
                  LOGIN
                </Button>
              )}
            </Box>
          )}
        </Box>
      </AppBar>
      {isLoggedIn && renderNavMenu()}
    </>
  );
}

export default NavBar;
